import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { VideoItem } from '@/types/video';
import { detailPath, typeLabel } from '@/data/videos';

interface HeroCarouselProps {
  items: VideoItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setCurrent((prev) => (prev + 1) % items.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) {
    return (
      <div className="flex h-[420px] items-center justify-center rounded-lg bg-background-200 text-sm text-foreground-500">
        暂无推荐内容
      </div>
    );
  }

  const goTo = (index: number) => setCurrent((index + items.length) % items.length);

  return (
    <section className="relative h-[460px] w-full overflow-hidden rounded-lg bg-primary-950 md:h-[560px]">
      {items.map((item, index) => {
        const active = index === current;
        return (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              active ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
            aria-hidden={!active}
          >
            <img
              src={item.backdrop ?? item.cover}
              alt={`${item.title}焦点海报`}
              title={`${item.title} - 精选推荐`}
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-transparent to-primary-950/25" />

            <Link
              to={detailPath(item)}
              aria-label={`查看《${item.title}》详情`}
              tabIndex={active ? 0 : -1}
              className="absolute inset-0 z-0 cursor-pointer"
            />

            <div className="pointer-events-none relative z-[5] flex h-full items-end px-6 pb-14 md:items-center md:px-12 md:pb-0">
              <div className={`w-full max-w-xl ${active ? 'animate-fade-up' : ''}`}>
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-accent-500 px-2 py-1 font-label text-[11px] font-bold tracking-wide text-primary-950">
                    编辑精选
                  </span>
                  <span className="rounded-md border border-background-50/30 px-2 py-1 text-[11px] text-background-50">
                    {typeLabel(item.type)}
                  </span>
                  <span className="flex items-center gap-1 rounded-md bg-primary-950/50 px-2 py-1 font-label text-[11px] font-semibold text-accent-300">
                    <i className="ri-star-fill text-[10px]" />
                    {item.rating.toFixed(1)}
                  </span>
                </div>

                <h2 className="font-heading text-3xl font-extrabold leading-tight tracking-tight text-background-50 md:text-[44px]">
                  {item.title}
                </h2>

                <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-background-200 md:text-sm">
                  <span>{item.year}</span>
                  <span className="h-0.5 w-0.5 rounded-full bg-background-200/70" />
                  <span>{item.regions.join(' / ')}</span>
                  <span className="h-0.5 w-0.5 rounded-full bg-background-200/70" />
                  <span>{item.genres.join(' · ')}</span>
                  <span className="h-0.5 w-0.5 rounded-full bg-background-200/70" />
                  <span>{item.duration}</span>
                </p>

                <p className="mt-4 hidden max-w-lg text-sm leading-relaxed text-background-200/90 sm:block md:line-clamp-2">
                  {item.synopsis}
                </p>

                <div className="pointer-events-auto mt-6 flex flex-wrap items-center gap-3">
                  <Link
                    to={detailPath(item)}
                    className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                  >
                    <i className="ri-play-fill text-lg" />
                    立即播放
                  </Link>
                  <Link
                    to={detailPath(item)}
                    className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:border-background-50 hover:bg-background-50/10"
                  >
                    <i className="ri-information-line text-base" />
                    查看详情
                  </Link>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div className="absolute bottom-5 right-5 z-10 flex items-center gap-3 md:bottom-7 md:right-8">
        <div className="flex items-center gap-1.5">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到 ${item.title}`}
              onClick={() => goTo(index)}
              className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
                index === current ? 'w-7 bg-accent-500' : 'w-1.5 bg-background-50/50 hover:bg-background-50/80'
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => goTo(current - 1)}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => goTo(current + 1)}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>
      </div>
    </section>
  );
}